import React, { useEffect } from 'react';
import styled from '@2pulse/responsive';
import { useForm } from 'react-hook-form';
import { normalize } from '@app/helpers';
import { 
  IPack, 
  ICatalogSnapshotToShop, 
  ICatalogSnapshotToShopPackOverride,
  convertToEuros
} from '@app/common';
import { Button, Form, Modal } from '@app/components';
import { CatalogShopPriceInput } from './CatalogShopPriceInput';
import { catalogSnapshotToShopApi } from '@app/store';
import { useToast } from '@app/hooks';

export const CatalogShopPackEditModalId = 'catalog-shop-pack-edit';

interface CatalogShopPackEditProps {
  isOpen: boolean;
  pack: IPack | null;
  catalogSnapshotToShop: ICatalogSnapshotToShop | undefined;
  onClose: () => void;
}

interface PackOverrideFormValues {
  packId: string;
  price: number | null;
  deliveryPrice: number | null;
  takeAwayPrice: number | null;
  onSpotPrice: number | null;
}

const transformToFormValues = (
  pack: IPack | null, 
  catalogSnapshotToShop: ICatalogSnapshotToShop | undefined
): PackOverrideFormValues => {
  if (!pack) {
    return {
      packId: '',
      price: null,
      deliveryPrice: null,
      takeAwayPrice: null,
      onSpotPrice: null,
    };
  }

  const existingOverride = catalogSnapshotToShop?.data?.packsOverrides?.find(
    override => override.packId === pack._id
  );

  return {
    packId: pack._id,
    price: existingOverride?.price ?? null,
    deliveryPrice: existingOverride?.deliveryPrice ?? null,
    takeAwayPrice: existingOverride?.takeAwayPrice ?? null,
    onSpotPrice: existingOverride?.onSpotPrice ?? null,
  };
};

export const CatalogShopPackEdit = ({ 
  isOpen, 
  pack, 
  catalogSnapshotToShop, 
  onClose 
}: CatalogShopPackEditProps) => {
  const toast = useToast();
  const [updateCatalogSnapshotToShop, { isLoading, error: apiErrors }] = catalogSnapshotToShopApi.useUpdateCatalogSnapshotToShopMutation();

  const formMethods = useForm<PackOverrideFormValues>({
    defaultValues: transformToFormValues(pack, catalogSnapshotToShop)
  });

  useEffect(() => {
    if (isOpen && pack) {
      formMethods.reset(transformToFormValues(pack, catalogSnapshotToShop));
    }
  }, [isOpen, pack, catalogSnapshotToShop]);

  const onSubmit = async (formValues: PackOverrideFormValues) => {
    if (!catalogSnapshotToShop) return;

    try {
      const existingPacksOverrides = catalogSnapshotToShop.data?.packsOverrides?.filter(
        override => override.packId !== formValues.packId
      ) || [];

      // Keep the existing disabled state
      const existingOverride = catalogSnapshotToShop.data?.packsOverrides?.find(
        override => override.packId === formValues.packId
      );

      const newPackOverride: ICatalogSnapshotToShopPackOverride = {
        packId: formValues.packId,
        disabled: existingOverride?.disabled ?? null,
        price: formValues.price,
        deliveryPrice: formValues.deliveryPrice,
        takeAwayPrice: formValues.takeAwayPrice,
        onSpotPrice: formValues.onSpotPrice,
      };

      await updateCatalogSnapshotToShop({
        _id: catalogSnapshotToShop._id,
        data: {
          ...catalogSnapshotToShop.data,
          packsOverrides: [...existingPacksOverrides, newPackOverride]
        }
      }).unwrap();

      toast.success('Prix mis à jour avec succès');
      onClose();
    } catch (error) {
      toast.error('Une erreur est survenue lors de la mise à jour');
    }
  };

  if (!isOpen || !pack) return null;

  return (
    <Modal id={CatalogShopPackEditModalId} isOpen={isOpen}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{pack.name}</ModalTitle>
          <ModalSubtitle>Modifier les prix du menu</ModalSubtitle>
        </ModalHeader>

        <Form formMethods={formMethods} apiErrors={apiErrors}>
          <ModalContent>
            <PriceGrid>
              <PriceItem>
                <PriceLabel>Prix de base</PriceLabel>
                <PriceRow>
                  <CatalogPrice>
                    Catalogue: {pack.price ? `${convertToEuros(pack.price).toFixed(2)} €` : '-'}
                  </CatalogPrice>
                  <CatalogShopPriceInput
                    name="price"
                    label="Prix shop"
                    catalogValue={pack.price}
                  />
                </PriceRow>
              </PriceItem>

              <PriceItem>
                <PriceLabel>Prix livraison</PriceLabel>
                <PriceRow>
                  <CatalogPrice>
                    Catalogue: {pack.deliveryPrice ? `${convertToEuros(pack.deliveryPrice).toFixed(2)} €` : '-'}
                  </CatalogPrice>
                  <CatalogShopPriceInput
                    name="deliveryPrice"
                    label="Prix shop"
                    catalogValue={pack.deliveryPrice}
                  />
                </PriceRow>
              </PriceItem>

              <PriceItem>
                <PriceLabel>Prix à emporter</PriceLabel>
                <PriceRow>
                  <CatalogPrice>
                    Catalogue: {pack.takeAwayPrice ? `${convertToEuros(pack.takeAwayPrice).toFixed(2)} €` : '-'}
                  </CatalogPrice>
                  <CatalogShopPriceInput
                    name="takeAwayPrice"
                    label="Prix shop"
                    catalogValue={pack.takeAwayPrice}
                  />
                </PriceRow>
              </PriceItem>

              <PriceItem>
                <PriceLabel>Prix sur place</PriceLabel>
                <PriceRow>
                  <CatalogPrice>
                    Catalogue: {pack.onSpotPrice ? `${convertToEuros(pack.onSpotPrice).toFixed(2)} €` : '-'}
                  </CatalogPrice>
                  <CatalogShopPriceInput
                    name="onSpotPrice"
                    label="Prix shop"
                    catalogValue={pack.onSpotPrice}
                  />
                </PriceRow>
              </PriceItem>
            </PriceGrid>
          </ModalContent>

          <ModalActions>
            <Button
              title="Annuler"
              onPress={onClose}
              bordered
              grow
              large
            />
            <Button
              title="Enregistrer"
              onPress={formMethods.handleSubmit(onSubmit)}
              loading={isLoading}
              grow
              large
            />
          </ModalActions>
        </Form>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled.View({
  backgroundColor: 'rgba(2,24,43,0.98)',
  borderRadius: normalize(16),
  width: '90%',
  maxWidth: 550,
  maxHeight: '90%',
});

const ModalHeader = styled.View({
  padding: normalize(24),
  borderBottomWidth: 1,
  borderBottomColor: 'rgba(255,255,255,0.1)',
});

const ModalTitle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(22),
  fontWeight: '700',
  color: theme.colors.babypowder,
  marginBottom: normalize(4),
}));

const ModalSubtitle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: theme.colors.satanGray,
}));

const ModalContent = styled.View({
  padding: normalize(24),
});

const PriceGrid = styled.View({
  gap: normalize(20),
});

const PriceItem = styled.View({
  gap: normalize(8),
});

const PriceLabel = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(12),
  fontWeight: '600',
  color: theme.colors.babypowder,
  textTransform: 'uppercase',
  letterSpacing: 1,
}));

const PriceRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: normalize(16),
});

const CatalogPrice = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(13),
  color: 'rgba(255,255,255,0.5)',
}));

const ModalActions = styled.View({
  flexDirection: 'row',
  gap: normalize(16),
  padding: normalize(24),
  borderTopWidth: 1,
  borderTopColor: 'rgba(255,255,255,0.1)',
});
